import { useCallback, useRef, useState } from "react";
import { streamChat } from "./api";
import type { CardListing, ChatMessage, Filters } from "../../shared/types";

export interface AssistantTurn {
  key: string;
  role: "assistant";
  text: string;
  filters: Filters | null;
  listings: CardListing[] | null;
  relaxed: boolean;
  exactCount: number;
  totalExactMatches: number;
  /** "search" shows result cards; "answer" is a knowledge-base reply. */
  intent: "search" | "answer";
  /** "searching" until cards land, then "streaming" until the reply finishes. */
  status: "searching" | "streaming" | "done";
  error?: string;
}

export interface UserTurn {
  key: string;
  role: "user";
  text: string;
}

export type Turn = UserTurn | AssistantTurn;

/**
 * Owns the conversation: the transcript sent to the server, the filters carried
 * between turns, and the render model for the thread. Lives in `App` so a trip
 * to a listing page and back doesn't lose it.
 */
export function useChat() {
  const [turns, setTurns] = useState<Turn[]>([]);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);

  // Not state: these are read inside the send loop, never rendered directly.
  const history = useRef<ChatMessage[]>([]);
  const filters = useRef<Filters>({});

  const reset = useCallback(() => {
    history.current = [];
    filters.current = {};
    setTurns([]);
    setSuggestions([]);
    setBusy(false);
  }, []);

  const send = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || busy) return;

      const key = String(Date.now());
      setBusy(true);
      setSuggestions([]);
      history.current = [...history.current, { role: "user", content: trimmed }];

      setTurns((prev) => [
        ...prev,
        { key: `u${key}`, role: "user", text: trimmed },
        {
          key: `a${key}`,
          role: "assistant",
          text: "",
          filters: null,
          listings: null,
          relaxed: false,
          exactCount: 0,
          totalExactMatches: 0,
          intent: "search",
          status: "searching",
        },
      ]);

      const patch = (change: Partial<AssistantTurn>) =>
        setTurns((prev) =>
          prev.map((t) => (t.key === `a${key}` && t.role === "assistant" ? { ...t, ...change } : t)),
        );

      let reply = "";

      try {
        for await (const event of streamChat(history.current, filters.current)) {
          switch (event.type) {
            case "intent":
              patch({ intent: event.intent });
              break;
            case "filters":
              filters.current = event.filters;
              patch({ filters: event.filters });
              break;
            case "listings":
              patch({
                listings: event.listings,
                relaxed: event.relaxed,
                exactCount: event.exactCount,
                totalExactMatches: event.totalExactMatches,
                status: "streaming",
              });
              break;
            case "token":
              reply += event.text;
              patch({ text: reply, status: "streaming" });
              break;
            case "suggestions":
              setSuggestions(event.suggestions);
              break;
            case "error":
              patch({ error: event.message });
              break;
            case "done":
              break;
          }
        }
      } catch (err) {
        console.error(err);
        patch({ error: "I couldn't reach the search service. Check the connection and try again." });
      }

      patch({ status: "done" });
      if (reply) history.current = [...history.current, { role: "assistant", content: reply }];
      setBusy(false);
    },
    [busy],
  );

  return { turns, suggestions, busy, send, reset };
}
